/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../../..../../contract-frontend/src/environments/environment';
import { AuthService } from '../auth.service';
import { RoutesValues } from '../../../common/enums/routes.enum';
import { StorageData } from '../../../common/models/storageData';
import { LanguageService } from '../../translations/services/language.service';
import {
  APPLICATION_NAME_HEADER,
  Applications,
  COMPANY_ID_HEADER,
  StateStoreKeys,
  StatesService,
  User,
  WorkspaceService,
} from '@socium/sociumflow';
import { OBSERVABILITY_HEADERS } from '@socium/common-api';

/**
 * Service that intercepts HTTP requests to add authentication and language headers.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly statesService = inject(StatesService);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly languageService = inject(LanguageService);
  private readonly translateService = inject(TranslateService);
  private userLang = this.languageService.getUserLang();
  private readonly subscription = new Subscription();

  constructor() {
    this.listenChangeLanguage();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.isS3Url(req.url)) {
      return next.handle(req);
    }

    if (this.isGuestRequest(req.url)) {
      return this.handleGuestRequest(req, next);
    }

    let modifiedRequest = req;

    const companyId =
      this.statesService.getCookie<string>(
        StateStoreKeys.SELECTED_COMPANY_ID,
      ) ??
      this.authService.getCompany()?._id ??
      '';
    const currentUser = this.statesService.getCurrentUser();
    const authorId = (currentUser as User | null)?._id ?? '';

    if (companyId) {
      modifiedRequest = modifiedRequest.clone({
        headers: modifiedRequest.headers.set(COMPANY_ID_HEADER, companyId),
      });
    }
    modifiedRequest = modifiedRequest.clone({
      headers: modifiedRequest.headers
        .set(APPLICATION_NAME_HEADER, Applications.CONTRACT)
        .set(OBSERVABILITY_HEADERS.CLIENT_ID, companyId)
        .set(OBSERVABILITY_HEADERS.AUTHOR_ID, authorId),
    });
    return this.handleAuthRequest(modifiedRequest, next);
  }

  private isS3Url(url: string): boolean {
    return url.includes(environment.s3Url);
  }

  private isGuestRequest(url: string): boolean {
    const guestUrls = [
      'guest/check-token-validity',
      'guest/contract',
      'guest/search-variables',
      'guest/signature-informations',
      'guest/put-signature-on-contract-by-guest',
    ];
    return this.valuePresentInArray(url, guestUrls);
  }

  private handleGuestRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (req.url.includes('check-token-validity')) {
      return this.handleCheckTokenValidity(req, next);
    }

    const tokenData = this.getGuestTokenData();
    if (!tokenData) {
      return next.handle(req);
    }

    if (this.isTokenExpired(tokenData.expireAt)) {
      this.clearGuestToken();
      this.redirectToGuestContracts();
      return next.handle(req);
    }

    return this.addGuestTokenToRequest(req, tokenData, next);
  }

  private handleCheckTokenValidity(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(
      req.clone({
        headers: req.headers
          .set('api-key', environment.apiKey)
          .set('accept-language', this.userLang),
      }),
    );
  }

  private getGuestTokenData(): any {
    return this.statesService.getCookie(StateStoreKeys.GUEST_TOKEN_NAME);
  }

  private isTokenExpired(expirationDate: string): boolean {
    return Date.now() > Date.parse(expirationDate);
  }

  private clearGuestToken(): void {
    this.statesService.clearCookie(StateStoreKeys.GUEST_TOKEN_NAME);
  }

  private redirectToGuestContracts(): void {
    window.location.href = `${RoutesValues.GUESTS}/${RoutesValues.CONTRACTS}`;
  }

  private addGuestTokenToRequest(
    req: HttpRequest<any>,
    tokenData: any,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(
      req.clone({
        headers: req.headers
          .set('Authorization', `Bearer ${tokenData.token}`)
          .set('api-key', environment.apiKey)
          .set('accept-language', this.userLang),
      }),
    );
  }

  private handleAuthRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const authData = this.getAuthTokenData();
    if (!authData) {
      return next.handle(req);
    }

    if (this.authService.isTokenExpired()) {
      this.clearAuthToken();
      this.redirectToLogin();
      return next.handle(req);
    }

    return this.addAuthTokenToRequest(req, authData, next);
  }

  private getAuthTokenData(): StorageData | null {
    return this.statesService.getCookie<StorageData>(StateStoreKeys.TOKEN_NAME);
  }

  private clearAuthToken(): void {
    this.workspaceService.clearSessionsStore();
  }

  private redirectToLogin(): void {
    window.location.href = environment.workspaceUrl;
  }

  private addAuthTokenToRequest(
    req: HttpRequest<any>,
    authData: StorageData,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const authReq = req.url.includes(environment.workSpaceEndpoint)
      ? req.clone({
          headers: req.headers
            .set('Authorization', `Bearer ${authData._accessToken}`)
            .set('accept-language', this.userLang),
        })
      : req.clone({
          headers: req.headers
            .set('Authorization', `Bearer ${authData._accessToken}`)
            .set('api-key', environment.apiKey)
            .set('accept-language', this.userLang),
        });
    return next.handle(authReq);
  }

  private valuePresentInArray(value: string, items: string[]): boolean {
    for (const item of items) {
      if (value.includes(item)) {
        return true;
      }
    }
    return false;
  }

  private listenChangeLanguage(): void {
    const translationSubscription =
      this.translateService.onLangChange.subscribe(
        (langChangeEvent: LangChangeEvent) => {
          this.userLang = langChangeEvent.lang;
        },
      );
    this.subscription.add(translationSubscription);
  }
}
